import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchConsoleBranding } from "../../api/consoleApi";
import type { ConsoleBranding } from "../../api/types";
import { useConsoleAuth } from "../../auth/AuthProvider";
import { STATIC_HEADER_ICON_PATH, STATIC_LOGO_PATH } from "../../utils/branding";

interface TenantContextType {
  tenantConfig: ConsoleBranding | null;
  loading: boolean;
  error: string | null;
  logoSrc: string;
  faviconSrc: string;
  /** True only after --brand-primary, --accent-color and --secondary-color are set. */
  ready: boolean;
}

export const TenantContext = createContext<TenantContextType>({
  tenantConfig: null,
  loading: false,
  error: null,
  logoSrc: STATIC_LOGO_PATH,
  faviconSrc: STATIC_HEADER_ICON_PATH,
  ready: false,
});

export const useTenantConfig = () => useContext(TenantContext);

function applyFavicon(href: string) {
  const faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
  if (faviconLink) {
    faviconLink.href = href;
    return;
  }
  const created = document.createElement("link");
  created.rel = "icon";
  created.href = href;
  document.head.appendChild(created);
}

function applyBrandTokens(config: ConsoleBranding): boolean {
  const primary = (config.primary_color || "").trim();
  const accent = (config.accent_color || "").trim();
  const secondary = (config.secondary_color || "").trim();
  if (!primary || !accent || !secondary) {
    return false;
  }
  document.documentElement.style.setProperty("--brand-primary", primary);
  document.documentElement.style.setProperty("--accent-color", accent);
  document.documentElement.style.setProperty("--secondary-color", secondary);
  return true;
}

/**
 * Product TenantThemeProvider: --brand-primary from primary_color, favicon from
 * favicon_path, document.title from page_title (UI-PARITY-SPEC section 1).
 * Section 10.5: ready is false until the three brand tokens are applied. The
 * fallback blue is never written. Section 10.6: header and empty-state images
 * are static /assets/ paths, never the branding image routes.
 */
export function TenantThemeProvider({ children }: { children: React.ReactNode }) {
  const [tenantConfig, setTenantConfig] = useState<ConsoleBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const { isAuthenticated, isLoading: authLoading, getIdToken, releaseStaleSession } =
    useConsoleAuth();

  useEffect(() => {
    let cancelled = false;
    const fetchTenantConfig = async () => {
      try {
        setError(null);
        setReady(false);
        if (!isAuthenticated || authLoading) {
          setLoading(false);
          return;
        }
        const token = await getIdToken();
        if (!token) {
          if (isAuthenticated) {
            releaseStaleSession();
          }
          setLoading(false);
          return;
        }
        const result = await fetchConsoleBranding(getIdToken);
        const config = result.body as ConsoleBranding;
        if (config.outcome_type === "blocked") {
          releaseStaleSession();
          setLoading(false);
          return;
        }
        if (config.outcome_type !== "success" || !("primary_color" in config)) {
          setError(config.message || "Cortex could not load the Cordexa brand settings.");
          setLoading(false);
          return;
        }
        if (cancelled) {
          return;
        }
        if (!applyBrandTokens(config)) {
          setError("Cortex could not load the Cordexa brand settings. Try again in a moment.");
          setLoading(false);
          return;
        }
        setTenantConfig(config);
        applyFavicon(STATIC_HEADER_ICON_PATH);
        if (typeof document !== "undefined") {
          document.title = "Cortex";
        }
        setReady(true);
      } catch {
        setError("Cortex could not load the Cordexa brand settings. Try again in a moment.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void fetchTenantConfig();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authLoading, getIdToken, releaseStaleSession]);

  return (
    <TenantContext.Provider
      value={{
        tenantConfig,
        loading,
        error,
        logoSrc: STATIC_LOGO_PATH,
        faviconSrc: STATIC_HEADER_ICON_PATH,
        ready,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}
